import { supabase } from '@/services/supabase';

/**
 * Fetches all permissions for a given user ID
 * @param userId - The user's UUID
 * @returns Array of permission names the user has access to
 */
export async function getUserPermissions(userId: string): Promise<string[]> {
  try {
    const permissions = new Set<string>();

    // First check if user has Superadmin role (42) in users table
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role_id')
      .eq('id', userId)
      .single();

    if (userError) {
      console.error('Error fetching user role:', userError);
    }

    // If user is Superadmin (role_id 42), grant system.override permission
    if (userData?.role_id === 42) {
      console.log('[getUserPermissions] User has Superadmin role (42), granting system.override');
      permissions.add('system.override');
    }

    // Then fetch additional permissions from user_roles table
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        role_id,
        roles!inner (
          role_permissions!inner (
            permissions!inner (
              name
            )
          )
        )
      `)
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user_roles permissions:', error);
    }

    // Flatten to array of unique permission names
    if (data) {
      data.forEach((userRole: any) => {
        const roleData = userRole.roles;
        if (roleData && roleData.role_permissions) {
          roleData.role_permissions.forEach((rp: any) => {
            if (rp.permissions && rp.permissions.name) {
              permissions.add(rp.permissions.name);
            }
          });
        }
      });
    }

    console.log('[getUserPermissions] Final permissions:', Array.from(permissions));
    return Array.from(permissions);
  } catch (error) {
    console.error('Failed to fetch user permissions:', error);
    return [];
  }
}

/**
 * Checks if a user has a specific permission
 * @param userPermissions - Array of permission names the user has
 * @param permissionName - The permission to check for
 * @returns True if user has the permission or system.override
 */
export function userHasPermission(
  userPermissions: string[],
  permissionName: string
): boolean {
  // Super admin bypass
  if (userPermissions.includes('system.override')) {
    return true;
  }

  return userPermissions.includes(permissionName);
}

/**
 * Checks if a user has any of the specified permissions
 * @param userPermissions - Array of permission names the user has
 * @param permissionNames - Array of permissions to check for
 * @returns True if user has at least one of the permissions
 */
export function userHasAnyPermission(
  userPermissions: string[],
  permissionNames: string[]
): boolean {
  // Super admin bypass
  if (userPermissions.includes('system.override')) {
    return true;
  }

  return permissionNames.some(permission => userPermissions.includes(permission));
}
